import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import CancelBooking, { CancelLinks } from '../components/portal/CancelBooking';
import FindAndBook from '../components/portal/FindAndBook';
import Reschedule from '../components/portal/Reschedule';
import PortalCard, { PortalLegal } from '../components/portal/PortalCard';
import PortalDeleteLogin from '../components/portal/PortalDeleteLogin';
import { useAuth } from '../context/AuthContext';
import { listPortalMemberships } from '../data/identity';
import {
  familyPortalBookPath, familyPortalChoices, familyPortalSchedulePath, familyPortalWord as w,
} from '../domain/familyPortal';
import { IDENTITY_ROLE, portalPath } from '../domain/identity';
import { decimalSeparatorFor, formatLongDate, formatTime } from '../domain/locale';
import { formatCredits } from '../domain/makeup';
import { addDays } from '../domain/recurrence';
import { zoneAbbreviation } from '../domain/timeZone';
import { useIdentityRole } from '../hooks/useIdentityRole';
import { usePortalView } from '../hooks/usePortalView';
import s from './PortalPage.module.css';

// /portal and /portal/schedule (#772): the Family Portal, signed in.
//
// ── What MMS draws, and what of it is here ──────────────────────────────────
//
// MMS's portal is the teacher's frame re-skinned: a sidebar of a dozen
// sections, a header with the person's name, Home with "Welcome back, Linh!",
// four tiles (lessons this week with Book a Lesson, hours practised,
// attendance in the last ninety days, the latest invoice), "Here's What's
// Scheduled This Week", recent notes and news; and Calendar → Schedule, a
// month grid with a child picker, Book Now and Sync Calendar.
//
// Here: the studio's name across the top, the family and a way to switch it,
// a student picker, and —
//
//   * Home: the greeting, "Lessons this week" (and the make-up credits, where
//     the studio shows them), MMS's "Here's What's Scheduled This Week", the
//     teacher's contact lines and the studio's Policy Text.
//   * Schedule: every lesson from today to the end of the view's window, day
//     by day. An agenda rather than MMS's month grid: a family's question is
//     "when is the next one", the window is a term rather than a month, and a
//     grid of mostly empty days is a poor read on a phone.
//   * Find & Book (#773), components/portal/FindAndBook.jsx.
//
// Cancel Booking (#775) is a link under each lesson the studio's policy lets
// the family cancel, and Reschedule (#543) beside it,
// components/portal/Reschedule.jsx. Practice, attendance, invoices, notes and
// news are not offered. None of the teacher app's frame appears: this is
// mounted outside it (components/portal/PortalApp.jsx).
//
// ── Studios and families ────────────────────────────────────────────────────
//
// A login may belong to several studios, and to several families in one. Each
// pair is one choice; with exactly one the picker is skipped, with more the
// person picks one and can switch from the header. The choice is remembered in
// this browser only — a fresh browser asks again. Every view is one studio's,
// and the server answers for one studio at a time.
//
// ── Times ───────────────────────────────────────────────────────────────────
//
// The server answers on the family's clock (functions/portal/view.js): date-
// only strings and `HH:MM`, written here with the studio's Region's words and
// clock (domain/locale.js), each time labelled with the family's zone at that
// lesson's own instant. No `Date` is built from a lesson.

const CHOICE_KEY = 'coda.portal.choice';

function readChoice(uid) {
  try {
    const held = JSON.parse(window.localStorage.getItem(CHOICE_KEY) || 'null');
    return held && held.uid === uid ? held.key : null;
  } catch {
    return null;
  }
}

function writeChoice(uid, key) {
  try {
    if (key) window.localStorage.setItem(CHOICE_KEY, JSON.stringify({ uid, key }));
    else window.localStorage.removeItem(CHOICE_KEY);
  } catch {
    // Storage refused (a private window): the picker simply asks again.
  }
}

export default function PortalPage({ tab = 'home' }) {
  const { user, logout } = useAuth();
  const role = useIdentityRole();
  const uid = user?.uid ?? null;
  const [list, setList] = useState({ uid: null, rows: null, failed: false });
  const [attempt, setAttempt] = useState(0);
  const [picked, setPicked] = useState(() => readChoice(uid));

  useEffect(() => {
    if (!uid) return undefined;
    let live = true;
    listPortalMemberships(uid).then(
      (rows) => { if (live) setList({ uid, rows, failed: false }); },
      (err) => {
        console.warn('[portal] could not list memberships:', err);
        if (live) setList({ uid, rows: null, failed: true });
      },
    );
    return () => { live = false; };
  }, [uid, attempt]);

  const mine = list.uid === uid ? list : { rows: null, failed: false };
  const choices = useMemo(() => familyPortalChoices(mine.rows || []), [mine.rows]);
  const choice = choices.length === 1 ? choices[0] : (choices.find((c) => c.key === picked) || null);

  const signOut = () => logout().then(() => {}, (err) => {
    console.error('[portal] sign-out failed:', err);
  });
  // A teacher who is also a parent came here from their studio, and goes back
  // the same way (#543 §3: one login, "My studio" and the portal).
  const studioLink = role === IDENTITY_ROLE.studio || role === IDENTITY_ROLE.both;
  const pick = (key) => { setPicked(key); writeChoice(uid, key); };
  // "Delete my login" (#774) only for a login that is no studio: a teacher
  // who is also a parent deletes from Business Settings, which takes the
  // studio too, and the server refuses this one for them anyway.
  const canDelete = role === IDENTITY_ROLE.portal;

  const footer = (
    <div className={s.cardActions}>
      {studioLink ? <Link className={s.linkButton} to="/">{w('myStudio')}</Link> : null}
      <button type="button" className={s.linkButton} onClick={signOut}>{w('signOut')}</button>
      {canDelete ? <PortalDeleteLogin /> : null}
    </div>
  );

  if (mine.failed) {
    return (
      <PortalCard>
        <p className={s.body}>{w('loadFailed')}</p>
        <div className={s.form}><Button onClick={() => setAttempt((n) => n + 1)}>{w('retry')}</Button></div>
        {footer}
      </PortalCard>
    );
  }
  if (mine.rows == null) return <PortalCard><p className={s.body}>{w('loading')}</p></PortalCard>;
  if (choices.length === 0) {
    return (
      <PortalCard>
        <p className={s.body}>{w('noAccess')}</p>
        <p className={s.body}>{w('noAccessHelp')}</p>
        {footer}
      </PortalCard>
    );
  }
  if (!choice) {
    return (
      <PortalCard title={w('chooseTitle')}>
        <p className={s.body}>{w('chooseLead')}</p>
        <ul className={s.choices}>
          {choices.map((c) => (
            <li key={c.key}>
              <button type="button" className={s.choice} onClick={() => pick(c.key)}>
                <span className={s.choiceStudio}>{c.studioName || w('joinStudioFallback')}</span>
                {c.familyName ? <span className={s.choiceFamily}>{c.familyName}</span> : null}
              </button>
            </li>
          ))}
        </ul>
        {footer}
      </PortalCard>
    );
  }

  return (
    <FamilyPortal
      key={choice.key}
      choice={choice}
      tab={tab}
      onSwitch={choices.length > 1 ? () => pick(null) : null}
      studioLink={studioLink}
      signOut={signOut}
      canDelete={canDelete}
    />
  );
}

function FamilyPortal({
  choice, tab, onSwitch, studioLink, signOut, canDelete,
}) {
  const { view, error, loading, retry } = usePortalView(choice);
  const [student, setStudent] = useState('all');
  // A booking (#773) lands on the Schedule, so the view is asked again — but
  // only once the family leaves Find & Book: asking while it is open would
  // unmount "Booking Complete!" behind the loading line.
  const [booked, setBooked] = useState(false);
  useEffect(() => {
    if (booked && tab !== 'book') {
      setBooked(false);
      retry();
    }
  }, [booked, tab, retry]);
  // Cancel Booking (#775): `{ lessonId, studentId }` while its dialog is
  // open; closing it after a cancel (or a refusal that says the page is out
  // of date) asks for the view again.
  const [cancelling, setCancelling] = useState(null);
  // Reschedule (#543): the same, `{ lessonId, studentId }` while open.
  const [moving, setMoving] = useState(null);
  useEffect(() => { setCancelling(null); setMoving(null); }, [tab]);
  const cancelLesson = cancelling && view ? view.lessons.find((l) => l.id === cancelling.lessonId) : null;
  const moveLesson = !cancelLesson && moving && view ? view.lessons.find((l) => l.id === moving.lessonId) : null;
  const only = (set) => (x) => { setCancelling(null); setMoving(null); set(x); };
  const links = { onCancel: only(setCancelling), onReschedule: only(setMoving) };
  const dialog = Boolean(cancelLesson || moveLesson);

  const header = (
    <header className={s.header}>
      <div className={s.headerMain}>
        <div className={s.studioName}>{view?.studio?.name || choice.studioName || w('joinStudioFallback')}</div>
        <div className={s.familyLine}>
          <span>{view?.family?.name || choice.familyName || ''}</span>
          {onSwitch ? <button type="button" className={s.linkButton} onClick={onSwitch}>{w('switch')}</button> : null}
        </div>
      </div>
      <div className={s.headerActions}>
        {studioLink ? <Link className={s.linkButton} to="/">{w('myStudio')}</Link> : null}
        <button type="button" className={s.linkButton} onClick={signOut}>{w('signOut')}</button>
      </div>
    </header>
  );

  let body;
  if (error) {
    body = (
      <section className={s.panel}>
        <p className={s.body}>{error.message}</p>
        {error.refusal === 'no_access' || error.refusal === 'closed' ? null : (
          <div className={s.form}><Button onClick={retry}>{w('retry')}</Button></div>
        )}
      </section>
    );
  } else if (loading || !view) {
    body = <section className={s.panel}><p className={s.body}>{w('loading')}</p></section>;
  } else {
    const shown = student === 'all' || !view.students.some((x) => x.id === student) ? 'all' : student;
    body = (
      <>
        <div className={s.toolbar}>
          <nav className={s.tabs} aria-label={w('name')}>
            <Link className={tab === 'home' ? s.tabActive : s.tab} to={portalPath()}>{w('navHome')}</Link>
            <Link className={tab === 'schedule' ? s.tabActive : s.tab} to={familyPortalSchedulePath()}>{w('navSchedule')}</Link>
            <Link className={tab === 'book' ? s.tabActive : s.tab} to={familyPortalBookPath()}>{w('navFindBook')}</Link>
          </nav>
          {view.students.length > 1 ? (
            <label className={s.picker}>
              <span>{w('studentPicker')}</span>
              <select className={s.select} value={shown} onChange={(e) => setStudent(e.target.value)}>
                <option value="all">{w('allStudents')}</option>
                {view.students.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </label>
          ) : null}
        </div>
        {cancelLesson ? (
          <CancelBooking
            key={`${cancelLesson.id}:${cancelling.studentId}`}
            view={view}
            choice={choice}
            lesson={cancelLesson}
            studentId={cancelling.studentId}
            onClose={() => setCancelling(null)}
            onCancelled={() => { setCancelling(null); retry(); }}
          />
        ) : null}
        {moveLesson ? (
          <Reschedule
            key={`${moveLesson.id}:${moving.studentId}`}
            view={view}
            choice={choice}
            lesson={moveLesson}
            studentId={moving.studentId}
            onClose={() => setMoving(null)}
            onRescheduled={() => { setMoving(null); retry(); }}
          />
        ) : null}
        {!dialog && tab === 'schedule' ? <Schedule view={view} student={shown} links={links} /> : null}
        {tab === 'book' ? <FindAndBook choice={choice} student={shown} onBooked={() => setBooked(true)} /> : null}
        {!dialog && tab !== 'schedule' && tab !== 'book' ? <Home view={view} student={shown} links={links} /> : null}
      </>
    );
  }

  return (
    <div className={s.shell}>
      {header}
      <main className={s.content}>
        {body}
        {/* Quiet, at the foot, above the legal links (#774). */}
        {canDelete ? <div className={s.cardActions}><PortalDeleteLogin /></div> : null}
        <PortalLegal />
      </main>
    </div>
  );
}

// The lessons the picker shows: every family student's, or one's.
function lessonsFor(view, student) {
  if (student === 'all') return view.lessons;
  return view.lessons
    .filter((l) => l.studentIds.includes(student))
    .map((l) => ({
      ...l,
      studentIds: [student],
      cancelledStudentIds: l.cancelledStudentIds.filter((id) => id === student),
      ...(l.cancel?.[student] ? { cancel: { [student]: l.cancel[student] } } : { cancel: undefined }),
      ...(l.reschedule?.[student] ? { reschedule: { [student]: l.reschedule[student] } } : { reschedule: undefined }),
    }));
}

const isCalledOff = (l) => l.studentIds.length > 0 && l.studentIds.every((id) => l.cancelledStudentIds.includes(id));

function Home({ view, student, links }) {
  const locale = view.studio.locale || undefined;
  const weekEnd = addDays(view.from, 6);
  const lessons = lessonsFor(view, student);
  const thisWeek = lessons.filter((l) => l.date >= view.from && l.date <= weekEnd);
  const taught = thisWeek.filter((l) => !isCalledOff(l));
  const greeting = view.you?.firstName ? `${w('welcomeBack')}, ${view.you.firstName}!` : `${w('welcomeBack')}!`;
  const students = student === 'all' ? view.students : view.students.filter((x) => x.id === student);
  const decimal = decimalSeparatorFor(locale);

  return (
    <>
      <h1 className={s.greeting}>{greeting}</h1>
      <div className={s.tiles}>
        <div className={s.tile}>
          <div className={s.tileValue}>{taught.length}</div>
          <div className={s.tileLabel}>{w('lessonsThisWeek')}</div>
        </div>
        {view.visibility.makeupCredits ? (
          <div className={s.tile}>
            <div className={s.tileLabel}>{w('makeupCredits')}</div>
            <ul className={s.creditList}>
              {students.map((x) => (
                <li key={x.id}>
                  {x.firstName || x.name}: {formatCredits(x.credits?.available ?? null, decimal)} {w('makeupAvailable')}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <section className={s.panel}>
        <h2 className={s.panelTitle}>{w('scheduledThisWeek')}</h2>
        {thisWeek.length === 0
          ? <p className={s.body}>{w('nothingThisWeek')}</p>
          : <LessonList lessons={thisWeek} view={view} links={links} />}
      </section>

      <StudioInfo view={view} />
    </>
  );
}

function Schedule({ view, student, links }) {
  const upcoming = lessonsFor(view, student).filter((l) => l.date >= view.today);
  return (
    <section className={s.panel}>
      <h2 className={s.panelTitle}>{w('upcoming')}</h2>
      {view.family.timeZone ? <p className={s.hint}>{w('timesIn')} {view.family.timeZone.replace(/_/g, ' ')}.</p> : null}
      {upcoming.length === 0
        ? <p className={s.body}>{w('nothingUpcoming')}</p>
        : <LessonList lessons={upcoming} view={view} links={links} />}
    </section>
  );
}

// Day by day: each date once, its lessons under it.
function LessonList({ lessons, view, links }) {
  const locale = view.studio.locale || undefined;
  const days = [];
  for (const l of lessons) {
    const last = days[days.length - 1];
    if (last && last.date === l.date) last.lessons.push(l);
    else days.push({ date: l.date, lessons: [l] });
  }
  return (
    <ol className={s.days}>
      {days.map((d) => (
        <li key={d.date} className={s.day}>
          <div className={s.dayTitle}>{formatLongDate(d.date, locale)}</div>
          <ul className={s.lessons}>
            {d.lessons.map((l) => <Lesson key={l.id} lesson={l} view={view} links={links} />)}
          </ul>
        </li>
      ))}
    </ol>
  );
}

function Lesson({ lesson, view, links }) {
  const locale = view.studio.locale || undefined;
  const zone = view.family.timeZone;
  const byId = new Map(view.students.map((x) => [x.id, x]));
  const calledOff = isCalledOff(lesson);
  let when = null;
  if (lesson.allDay) when = w('allDay');
  else if (lesson.startTime) {
    const label = zone ? zoneAbbreviation(zone, lesson.date, lesson.startTime, locale || 'en-US') : '';
    when = `${formatTime(lesson.startTime, locale)} – ${formatTime(lesson.endTime, locale)}${label ? ` ${label}` : ''}`;
  }
  const names = lesson.studentIds.map((id) => {
    const x = byId.get(id);
    const name = x?.firstName || x?.name || '';
    return !calledOff && lesson.cancelledStudentIds.includes(id) ? `${name} (${w('cancelled')})` : name;
  });
  return (
    <li className={calledOff ? s.lessonOff : s.lesson}>
      <div className={s.lessonWhen}>
        {when}
        {calledOff ? <span className={s.badge}>{w('cancelled')}</span> : null}
      </div>
      <div className={s.lessonWho}>{[names.join(', '), lesson.category].filter(Boolean).join(' · ')}</div>
      {lesson.others?.length ? <div className={s.lessonMore}>{lesson.others.join(', ')}</div> : null}
      {lesson.description ? <div className={s.lessonMore}>{lesson.description}</div> : null}
      <CancelLinks lesson={lesson} view={view} onCancel={links?.onCancel} onReschedule={links?.onReschedule} />
    </li>
  );
}

function StudioInfo({ view }) {
  const { email, phone, address, policyText } = view.studio;
  return (
    <>
      {email || phone || address ? (
        <section className={s.panel}>
          <h2 className={s.panelTitle}>{w('contactTeacher')}</h2>
          <dl className={s.contact}>
            {email ? (<><dt>{w('contactEmail')}</dt><dd><a href={`mailto:${email}`}>{email}</a></dd></>) : null}
            {phone ? (<><dt>{w('contactPhone')}</dt><dd><a href={`tel:${phone}`}>{phone}</a></dd></>) : null}
            {address ? (<><dt>{w('contactAddress')}</dt><dd className={s.preLine}>{address}</dd></>) : null}
          </dl>
        </section>
      ) : null}
      {policyText ? (
        <section className={s.panel}>
          <h2 className={s.panelTitle}>{w('policies')}</h2>
          <p className={`${s.body} ${s.preLine}`}>{policyText}</p>
        </section>
      ) : null}
    </>
  );
}
